import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import apiClient from '../../api/client';

interface DnsVerifyErrorResponse {
  error?: string;
  hostname?: string;
  expected_cname?: string;
  found?: string;
}

interface DnsErrorState {
  error: string;
  hostname: string | null;
  expectedCname: string | null;
  foundCname: string | null;
  forStatus: string | null; // Track which status the error is for
}

interface UosCustomDomainMigrationProps {
  subscriptionId: string;
  /** LIVE custom hostnames on the subscription (not the migration-time snapshot). */
  customHostnames: string[];
  uosHostname: string;
  status: 'pending' | 'dns_verified' | 'provisioning' | 'active' | 'failed' | null;
  error?: string | null;
}

function HostnameList({ hostnames, className }: { hostnames: string[]; className: string }) {
  return (
    <ul className={className}>
      {hostnames.map((h) => (
        <li key={h}>{h}</li>
      ))}
    </ul>
  );
}

export function UosCustomDomainMigration({
  subscriptionId,
  customHostnames,
  uosHostname,
  status,
  error
}: UosCustomDomainMigrationProps) {
  const [dnsErrorState, setDnsErrorState] = useState<DnsErrorState | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const queryClient = useQueryClient();

  // Only show DNS error if it was set for the current status
  const dnsError = dnsErrorState?.forStatus === status ? dnsErrorState.error : null;
  const dnsErrorHostname = dnsErrorState?.forStatus === status ? dnsErrorState.hostname : null;
  const expectedCname = dnsErrorState?.forStatus === status ? dnsErrorState.expectedCname : null;
  const foundCname = dnsErrorState?.forStatus === status ? dnsErrorState.foundCname : null;

  const verifyDnsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(
        `/api/v1/subscriptions/${subscriptionId}/custom-domain-migration/verify-dns`
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        setDnsErrorState(null);
        queryClient.invalidateQueries({ queryKey: ['migration-status', subscriptionId] });
      } else {
        setDnsErrorState({
          error: data.error,
          hostname: data.hostname ?? null,
          expectedCname: data.expected_cname,
          foundCname: data.found,
          forStatus: status
        });
      }
    },
    onError: (err: AxiosError<DnsVerifyErrorResponse>) => {
      setDnsErrorState({
        error: err.response?.data?.error || 'Failed to verify DNS',
        hostname: err.response?.data?.hostname || null,
        expectedCname: err.response?.data?.expected_cname || null,
        foundCname: err.response?.data?.found || null,
        forStatus: status
      });
    }
  });

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(uosHostname);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopyError(true);
    }
  };

  // Active state — a done line, like the Remote Access step's
  if (status === 'active') {
    return (
      <div className="flex items-start gap-1.5">
        <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <div>
          {customHostnames.length === 1 ? (
            <>Your custom domain <strong>{customHostnames[0]}</strong> is now working on your UOS service.</>
          ) : (
            <>Your custom domains are now working on your UOS service:</>
          )}
          {customHostnames.length > 1 && (
            <HostnameList hostnames={customHostnames} className="list-disc list-inside mt-1" />
          )}
        </div>
      </div>
    );
  }

  // Provisioning state
  if (status === 'provisioning' || status === 'dns_verified') {
    return (
      <div className="mt-1 rounded-md border border-blue-200 bg-blue-50 p-2.5 text-blue-900">
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="font-medium">Setting up the TLS certificate</p>
        </div>
        <p className="mt-1">
          {customHostnames.length === 1 ? (
            <>Your custom domain <strong>{customHostnames[0]}</strong> is being configured. This usually takes 1-2 minutes.</>
          ) : (
            <>Your custom domains are being configured. This usually takes 1-2 minutes.</>
          )}
        </p>
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <div className="mt-1 rounded-md border border-red-200 bg-red-50 p-2.5 text-red-900">
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <p className="font-medium">Custom domain setup failed</p>
        </div>
        <p className="mt-1">
          {error || 'An error occurred while setting up your custom domain.'}
        </p>
        <button
          onClick={() => verifyDnsMutation.mutate()}
          disabled={verifyDnsMutation.isPending}
          className="mt-3 px-5 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {verifyDnsMutation.isPending ? 'Retrying...' : 'Retry'}
        </button>
      </div>
    );
  }

  // Default: pending status — an action-needed box, like the Remote Access step's
  return (
    <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-amber-900">
      <p className="font-medium">
        {customHostnames.length === 1 ? 'Your custom domain needs to be updated:' : 'Your custom domains need to be updated:'}
      </p>
      <HostnameList hostnames={customHostnames} className="font-medium mt-1 space-y-0.5" />

      <p className="mt-2">
        Update your DNS CNAME record to point to:
      </p>
      <div className="flex items-center gap-2 mt-1">
        <code className="bg-white border border-amber-200 px-3 py-1.5 rounded font-mono flex-1">
          {uosHostname}
        </code>
        <button
          onClick={copyToClipboard}
          className="p-1.5 text-amber-700 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
          title={copied ? 'Copied!' : 'Copy to clipboard'}
          aria-label="Copy hostname to clipboard"
        >
          {copied ? (
            <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      </div>

      {copyError && (
        <p className="text-xs mt-2" role="alert">
          Copy failed. Please select the text and copy manually.
        </p>
      )}

      {dnsError === 'dns_legacy_cname' && (
        // Resolves today through the pre-migration <slug>.cloudunifi.com name; we still
        // want the record pointed straight at the UOS hostname.
        <div className="mt-3 p-2.5 bg-white border border-amber-300 rounded">
          <p className="font-medium">
            {dnsErrorHostname ?? 'Your custom domain'} still points to your old hostname
          </p>
          <p className="mt-1">
            Found: <code className="bg-amber-50 border border-amber-200 px-1 rounded">{foundCname}</code>
          </p>
          <p className="mt-1">
            It still works, but please change the CNAME record to{' '}
            <code className="bg-amber-50 border border-amber-200 px-1 rounded">{expectedCname}</code>, then
            check again.
          </p>
          <p className="mt-2 text-xs">
            DNS changes can take up to 48 hours to propagate.
          </p>
        </div>
      )}

      {dnsError && dnsError !== 'dns_legacy_cname' && (
        <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded text-red-900">
          <p className="font-medium">
            DNS not configured correctly{dnsErrorHostname ? ` for ${dnsErrorHostname}` : ''}
          </p>
          <p className="mt-1">
            Expected CNAME: <code className="bg-white border border-red-200 px-1 rounded">{expectedCname}</code>
          </p>
          <p>
            Found: <code className="bg-white border border-red-200 px-1 rounded">{foundCname || 'No CNAME record found'}</code>
          </p>
          <p className="mt-2 text-xs">
            DNS changes can take up to 48 hours to propagate.
          </p>
        </div>
      )}

      <button
        onClick={() => verifyDnsMutation.mutate()}
        disabled={verifyDnsMutation.isPending}
        className="mt-3 px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {verifyDnsMutation.isPending ? 'Checking...' : 'Check DNS Status'}
      </button>
    </div>
  );
}
